export interface PasswordStrengthResult {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: string;
}

const COMMON_SEQUENCES = [
  '123456',
  'abcdef',
  'qwerty',
  'password',
  '111111',
  '000000',
  'abc123',
  'letmein',
  'welcome',
  'monkey',
  'dragon',
  'master',
  'admin',
  'login',
];

const KEYBOARD_PATTERNS = [
  'qwerty',
  'asdfgh',
  'zxcvbn',
  'qazwsx',
  '123456',
  '654321',
  'abcdef',
  'fedcba',
];

export function analyzePasswordStrength(password: string): PasswordStrengthResult {
  if (!password) {
    return { score: 0, label: 'veryWeak', color: '#ff4d4f' };
  }

  let score = 0;

  // Length checks
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 20) score += 1;

  // Character variety
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSymbols = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password);

  const varietyCount = [hasLowercase, hasUppercase, hasNumbers, hasSymbols].filter(Boolean).length;
  score += varietyCount;

  // Penalties for weak patterns
  const lowerPassword = password.toLowerCase();

  // Common sequences penalty
  for (const seq of COMMON_SEQUENCES) {
    if (lowerPassword.includes(seq)) {
      score -= 2;
      break;
    }
  }

  // Keyboard patterns penalty
  for (const pattern of KEYBOARD_PATTERNS) {
    if (lowerPassword.includes(pattern)) {
      score -= 1;
      break;
    }
  }

  // Repeated characters penalty
  if (/(.)\1{2,}/.test(password)) {
    score -= 1;
  }

  // Sequential numbers penalty (123, 234, etc.)
  if (/(?:012|123|234|345|456|567|678|789|890)/.test(password)) {
    score -= 1;
  }

  // All same character type penalty
  if (varietyCount === 1 && password.length < 16) {
    score -= 1;
  }

  // Normalize score to 0-4 range
  score = Math.max(0, Math.min(4, Math.round(score / 2)));

  const results: { label: string; color: string }[] = [
    { label: 'veryWeak', color: '#ff4d4f' },
    { label: 'weak', color: '#ff7a45' },
    { label: 'fair', color: '#ffc53d' },
    { label: 'strong', color: '#73d13d' },
    { label: 'veryStrong', color: '#52c41a' },
  ];

  const result = results[score] ?? { label: 'veryWeak', color: '#ff4d4f' };

  return {
    score: score as 0 | 1 | 2 | 3 | 4,
    label: result.label,
    color: result.color,
  };
}

export interface GeneratePasswordOptions {
  length?: number;
  includeSymbols?: boolean;
}

export function generatePassword(options: GeneratePasswordOptions = {}): string {
  const { length = 20, includeSymbols = true } = options;

  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

  let allChars = lowercase + uppercase + numbers;
  if (includeSymbols) {
    allChars += symbols;
  }

  // Ensure at least one of each type
  let password = '';
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  if (includeSymbols) {
    password += symbols[Math.floor(Math.random() * symbols.length)];
  }

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');
}
